import { useState } from "react";
import { useConfig } from "../../contexts/ConfigContext";
import { QuestionTypes, useEvaluationData } from "../../contexts/EvaluationContext";
import EvaluationFrame from "./EvaluationFrame";

const QUESTION_TYPE_MAX = 2;

const EvaluationSession = ({ onSessionComplete }) => {
  const { evalData, dispatch } = useEvaluationData();
  const { config } = useConfig();
  const { session } = evalData;
  const { answers, questions } = session;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onCancel = () => {
    const confirmCancel = window.confirm(
      "Are you sure you want to cancel? Be aware, to protect your privacy, all session data will be deleted."
    );
    if (confirmCancel) {
      window.location.href = "/evaluation/" + evalData.evaluation.slug;
    }
  };

  const getNextQuestion = (session) => {
    const firstQuestionType = QuestionTypes[0];
    let { questions, currentSequence, endSequence } = session;
    currentSequence += 1;
    while (currentSequence <= endSequence) {
      const nextQuestion = questions[currentSequence]?.[firstQuestionType];
      if (nextQuestion) {
          return [nextQuestion, currentSequence, 0];
      }
      currentSequence += 1;
    }
    return null;
  };

  const findQuestionByIndex = (questions, targetIndex) => {
    return Object.values(questions)
      .flatMap(sequence => Object.values(sequence))
      .find(question => question.index === targetIndex);
  };

  const onPrevious = () => {
    const { currentQuestion, progress } = session;
    let prevQuestion = null;
    const currentIndex = currentQuestion.index;
    if (currentIndex > 0) {
      prevQuestion = findQuestionByIndex(questions, currentIndex - 1);
   
      if (prevQuestion)
      {
        dispatch({
            type: "SET_CURRENT_QUESTION",
            question: prevQuestion,
            sequence: prevQuestion.sequence,
            questionType: prevQuestion.questionType,
        });
      }
    }
  };

  const onNext = async () => {
    let nextQuestion = null;
    let nextSequence = session.currentSequence;
    let nextQuestionType = session.currentQuestionType;
    const question = session.currentQuestion;
    const answer   = answers?.[question?.id] ?? null;

    setIsSubmitting(true);
    const answerPosted = await postAnswer(question, answer);
    setIsSubmitting(false);

    if (!answerPosted)
    {
      window.confirm(
        "There was an error submitting your answer. Please check your connection and try again."
      );
      return;
    }
    
    if (answer && answer.score <= question.threshold && nextQuestionType < QUESTION_TYPE_MAX) {
      // Try next questionType in same sequence
      nextQuestionType += 1;
      nextQuestion = session.questions[nextSequence][QuestionTypes[nextQuestionType]];
    }

    // If no next question in current sequence/type, go to next sequence
    if (!nextQuestion) {
      const result = getNextQuestion(session);
      if (result) {
        const [q, seq, type] = result;
        nextQuestion = q;
        nextSequence = seq;
        nextQuestionType = type;
      }
    }

    if (nextQuestion) {
      let progress = Object.keys(answers).reduce((acc, qid) => {
        const ans = answers[qid];
        const question = session.questionsById[qid];

        if (question.questionType === 0)
        {
          if (ans.score > question.threshold) {
            return acc + 3;
          } else {
            return acc + 1;
          }
        } else if (question.questionType === 1) {
          if (ans.score > question.threshold) {
            return acc + 2;
          } else {
            return acc + 1;
          }
        } else {
          return acc + 1;
        }
      }, 0);

      if (nextQuestion.index < 0) {
        nextQuestion.index = session.currentQuestion.index + 1;
      }
      dispatch({
        type: "SET_CURRENT_QUESTION",
        question: nextQuestion,
        sequence: nextSequence,
        questionType: nextQuestionType,
        progress: progress,
      });
    } else {
      onSessionComplete();
    }
  };
  
  const postAnswer = async (question, answer) => {
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    // Create AbortController for timeout
    const controller = new AbortController();
    try {
      const res = await fetch(
        `${config.apiBase}/session/response`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId:  session.id,
            questionId: question.id,
            answerId:   answer ? answer.id : 9999,
          }),
          signal: controller.signal // Add abort signal
        }
      );

      if (!res.ok) {
        // Check if response has content before trying to parse JSON
        let errorMessage = `Server error: ${res.status}`;
        if (res.headers.get('content-type')?.includes('application/json')) {
          const j = await res.json();
          errorMessage = j?.error || errorMessage;
        }
        throw new Error(errorMessage);
      }

      return true;

    } catch (error) {
      console.error('Failed to submit answer:', error);
      
      if (error.name === 'AbortError') {
        alert('Request timed out. Please check your connection and try again.');
      } else {
        alert(`Failed to save answer: ${error.message}`);
      }

      return false;

    } finally {
      clearTimeout(timeoutId);
    }
  };

  const handleAnswer = async (question, answer) => {
    console.log('Handling answer for question ID:', question.id, 'with answer:', answer);
    dispatch({ 
      type: "SET_ANSWER", 
      questionId: question.id, 
      answer: answer,
    });
  };

  // TODO On previous/next, should be configured as part of the evaluation
  return (
    <div>
      <EvaluationFrame 
          session={session}
          handleAnswer={handleAnswer}
          onCancel={onCancel}
          onPrevious={onPrevious}
          onNext={onNext}
          waiting={isSubmitting}
     />
    </div>
  );
};

export default EvaluationSession;
